import * as React from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/molecules/Table';
import { Button } from '@/components/atoms/Button';
import { Spinner } from '@/components/atoms/Spinner';
import { cn } from '@/utils/cn';
import styles from './DataTable.module.scss';
import type { DataTableProps, SortConfig } from './DataTable.types';

export function DataTable<T>({
  columns,
  data,
  onRowClick,
  isLoading = false,
  emptyMessage = 'No data available',
  className,
  sortBy,
  sortOrder = 'asc',
  onSort,
  page = 1,
  pageSize = 30,
  totalItems,
  onPageChange,
}: DataTableProps<T>) {
  const [internalSort, setInternalSort] = React.useState<SortConfig | null>(null);
  const [internalPage, setInternalPage] = React.useState(1);
  const isControlledPage = onPageChange !== undefined;
  const currentPage = isControlledPage ? page : internalPage;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentSort = sortBy ? { key: sortBy, order: sortOrder } : internalSort;

  const handleSort = (columnKey: string) => {
    const newOrder =
      currentSort?.key === columnKey && currentSort.order === 'asc' ? 'desc' : 'asc';

    if (onSort) {
      onSort(columnKey, newOrder);
    } else {
      setInternalSort({ key: columnKey, order: newOrder });
    }
  };

  const sortedData = React.useMemo(() => {
    if (!currentSort || onSort) {
      return data;
    }

    const sorted = [...data];
    const column = columns.find((col) => col.key === currentSort.key);
    if (!column) return sorted;

    sorted.sort((a, b) => {
      const aValue = column.accessor(a);
      const bValue = column.accessor(b);

      // Handle string, number, and date comparisons
      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      }

      return currentSort.order === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [data, currentSort, columns, onSort]);

  const getSortIcon = (columnKey: string) => {
    if (currentSort?.key !== columnKey) {
      return <ChevronsUpDown className={styles.dataTable__sortIcon} />;
    }
    return currentSort.order === 'asc' ? (
      <ChevronUp className={styles.dataTable__sortIcon} />
    ) : (
      <ChevronDown className={styles.dataTable__sortIcon} />
    );
  };

  // Pagination calculations
  const totalCount = totalItems ?? sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const showPagination = totalCount > pageSize;

  // When controlled (onPageChange provided), parent passes already-paginated data; slice only for internal pagination.
  const displayData =
    showPagination && !isControlledPage
      ? sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
      : sortedData;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const handlePageChange = (newPage: number) => {
    const clamped = Math.max(1, Math.min(newPage, totalPages));
    if (isControlledPage && onPageChange) {
      onPageChange(clamped);
    } else {
      setInternalPage(clamped);
    }
  };

  // Reset internal page when data changes (e.g. filters) and current page is out of bounds
  React.useEffect(() => {
    if (!isControlledPage && currentPage > totalPages && totalPages > 0) {
      setInternalPage(1);
    }
  }, [isControlledPage, currentPage, totalPages]);

  // Page numbers to show (e.g. [1, 2, 3, '...', 10] or [1, '...', 4, 5, 6, '...', 10])
  const pageNumbers = React.useMemo(() => {
    const delta = 2; // pages to show around current
    const range: (number | 'ellipsis')[] = [];
    const rangeStart = Math.max(2, currentPage - delta);
    const rangeEnd = Math.min(totalPages - 1, currentPage + delta);

    range.push(1);
    if (rangeStart > 2) range.push('ellipsis');
    for (let i = rangeStart; i <= rangeEnd; i++) {
      if (i !== 1 && i !== totalPages) range.push(i);
    }
    if (rangeEnd < totalPages - 1) range.push('ellipsis');
    if (totalPages > 1) range.push(totalPages);

    return range;
  }, [currentPage, totalPages]);

  if (isLoading) {
    return (
      <div className={cn(styles.dataTable, className)}>
        <div className={styles.dataTable__loading}>
          <Spinner size="lg" />
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  if (sortedData.length === 0) {
    return (
      <div className={cn(styles.dataTable, className)}>
        <div className={styles.dataTable__empty}>
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(styles.dataTable, className)}>
      <div className={styles.dataTable__wrapper}>
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  style={{ width: column.width, textAlign: column.align || 'left' }}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      className={styles.dataTable__sortButton}
                      data-no-press-scale=""
                      onClick={() => handleSort(column.key)}
                    >
                      {column.header}
                      {getSortIcon(column.key)}
                    </button>
                  ) : (
                    column.header
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayData.map((row, index) => (
              <TableRow
                key={index}
                className={onRowClick ? styles['dataTable__row--clickable'] : undefined}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    style={{ textAlign: column.align || 'left' }}
                  >
                    {column.accessor(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {showPagination && (
        <div className={styles.dataTable__pagination}>
          <p className={styles.dataTable__paginationInfo}>
            Mostrando {startItem}-{endItem} de {totalCount}
          </p>
          <div className={styles.dataTable__paginationButtons}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>
            <div className={styles.dataTable__pageNumbers}>
              {pageNumbers.map((p, i) =>
                p === 'ellipsis' ? (
                  <span key={`ellipsis-${i}`} className={styles.dataTable__pageEllipsis}>
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    className={cn(
                      styles.dataTable__pageBtn,
                      p === currentPage && styles['dataTable__pageBtn--active']
                    )}
                    data-no-press-scale=""
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
                )
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
